import React, { useState } from "react";
import {
  Box, Button, Divider, Drawer, DrawerBody, DrawerCloseButton,
  DrawerContent, DrawerHeader, DrawerOverlay, Heading, HStack,
  Text, useDisclosure, VStack,
} from "@chakra-ui/react";

const TOP_BAR_BLUE = "#00005f";
const PANEL_W = 280;

function PanelContent({ rules, payouts, totalPot }) {
  return (
    <VStack align="stretch" spacing={4}>
      <Heading size="sm" color={TOP_BAR_BLUE}>Rules</Heading>
      {rules && rules.length > 0 ? (
        <VStack align="stretch" spacing={3}>
          {rules.map((rule, i) => (
            <Box key={i}>
              {rule.heading && (
                <Text fontSize="xs" fontWeight="bold" color={TOP_BAR_BLUE}
                  textTransform="uppercase" letterSpacing="0.5px" mb={1}>
                  {rule.heading}
                </Text>
              )}
              {rule.lines.map((line, j) => (
                <Text key={j} fontSize="xs" color="gray.700" lineHeight="1.6">
                  • {line}
                </Text>
              ))}
            </Box>
          ))}
        </VStack>
      ) : (
        <Text fontSize="xs" color="gray.400">No rules set for this pool.</Text>
      )}

      <Divider />

      <Heading size="sm" color={TOP_BAR_BLUE}>Payouts</Heading>
      {totalPot != null && (
        <HStack justify="space-between" pb={1} borderBottom="2px solid" borderColor={TOP_BAR_BLUE}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">Total Pot</Text>
          <Text fontSize="sm" fontWeight="bold" color={TOP_BAR_BLUE}>
            ${Number(totalPot).toFixed(2)}
          </Text>
        </HStack>
      )}
      {payouts.length > 0 ? (
        <VStack align="stretch" spacing={1}>
          {payouts.map((p, i) => (
            <HStack key={i} justify="space-between" py={1}
              borderBottom="1px solid" borderColor="gray.100">
              <Text fontSize="xs" color="gray.700">{p.label}</Text>
              <Text fontSize="xs" fontWeight="bold" color="gray.800">
                ${Number(p.amount).toFixed(2)}
              </Text>
            </HStack>
          ))}
        </VStack>
      ) : (
        <Text fontSize="xs" color="gray.400">No payouts configured.</Text>
      )}
    </VStack>
  );
}

export default function RulesPayoutsPanel({ rules, payouts = [], totalPot }) {
  const [open, setOpen] = useState(false);
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  return (
    <>
      {/* ── Desktop: slide-in side panel ── */}
      <Box
        position="fixed"
        right={0}
        top="50%"
        transform="translateY(-50%)"
        zIndex={1000}
        display={{ base: "none", md: "flex" }}
        alignItems="center"
      >
        {/* Vertical tab */}
        <Box
          onClick={() => setOpen((o) => !o)}
          cursor="pointer"
          color="white"
          border="1px solid rgba(255,255,255,0.4)"
          bg="orange.400"
          _hover={{ bg: "orange.500" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          px="4px"
          py="10px"
          borderTopLeftRadius="sm"
          borderBottomLeftRadius="sm"
          flexShrink={0}
          userSelect="none"
          fontSize="14px"
          fontWeight="semibold"
          letterSpacing="0.5px"
          boxShadow="-2px 0 6px rgba(0,0,0,0.3)"
        >
          <Text
            style={{ writingMode: "vertical-rl", textOrientation: "mixed", transform: "rotate(180deg)" }}
            whiteSpace="nowrap"
          >
            Rules &amp; Payouts
          </Text>
        </Box>

        {/* Sliding panel */}
        <Box
          w={open ? `${PANEL_W}px` : "0px"}
          overflowX="hidden"
          transition="width 0.25s ease"
          bg="white"
          borderLeft={`3px solid ${TOP_BAR_BLUE}`}
          boxShadow="-4px 0 16px rgba(0,0,0,0.15)"
        >
          <Box w={`${PANEL_W}px`} maxH="85vh" overflowY="auto" p={5}>
            <PanelContent rules={rules} payouts={payouts} totalPot={totalPot} />
          </Box>
        </Box>
      </Box>

      {/* ── Mobile: floating button + bottom drawer ── */}
      <Button
        display={{ base: "flex", md: "none" }}
        position="fixed"
        bottom={4}
        right={4}
        zIndex={1000}
        size="sm"
        bg="orange.400"
        color="white"
        _hover={{ bg: "orange.500" }}
        boxShadow="0 2px 8px rgba(0,0,0,0.3)"
        borderRadius="full"
        px={4}
        onClick={onDrawerOpen}
      >
        Rules &amp; Payouts
      </Button>

      <Drawer isOpen={isDrawerOpen} onClose={onDrawerClose} placement="bottom">
        <DrawerOverlay />
        <DrawerContent maxH="80vh" borderTopRadius="lg">
          <DrawerCloseButton />
          <DrawerHeader color={TOP_BAR_BLUE} borderBottom="1px solid" borderColor="gray.200">
            Rules &amp; Payouts
          </DrawerHeader>
          <DrawerBody overflowY="auto" py={4}>
            <PanelContent rules={rules} payouts={payouts} totalPot={totalPot} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
